import { useEffect, useMemo } from 'react';
import { useT } from '@/i18n';
import { useGameStore } from '@/state/gameStore';
import { useLeaderboardStore } from '@/state/leaderboardStore';
import type { LeaderboardCategory, LeaderboardEntry } from '@/state/leaderboardStore';
import { PlayerProfileOverlay } from '@/ui/components/PlayerProfileOverlay';
import { PlayerAvatar } from '@/ui/components/PlayerAvatar';
import './LeaderboardScreen.css';

const RANK_MEDAL: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };

function formatValue(entry: LeaderboardEntry, cat: LeaderboardCategory): string {
  if (cat === 'item_power')   return `✦ ${entry.itemPower}`;
  if (cat === 'level')        return `Lv ${entry.level}`;
  if (cat === 'vault_tier')   return `Tier ${entry.highestVaultTier}`;
  if (cat === 'endless_wave') return `Welle ${entry.bestEndlessWave}`;
  return `${entry.credits.toLocaleString('de-DE')} ◈`;
}

function formatSecondary(entry: LeaderboardEntry, cat: LeaderboardCategory): string {
  if (cat === 'item_power')   return `Lv ${entry.level}`;
  if (cat === 'vault_tier')   return `Lv ${entry.level}`;
  if (cat === 'credits')      return `Lv ${entry.level}`;
  if (cat === 'endless_wave') return `Lv ${entry.level}`;
  return `Tier ${entry.highestVaultTier}`;
}

export function LeaderboardScreen() {
  const t = useT();
  const myId = useGameStore((s) => s.player?.id ?? null);

  const entries            = useLeaderboardStore((s) => s.entries);
  const category           = useLeaderboardStore((s) => s.category);
  const loading            = useLeaderboardStore((s) => s.loading);
  const error              = useLeaderboardStore((s) => s.error);
  const myRank             = useLeaderboardStore((s) => s.myRank);
  const lastFetchAt        = useLeaderboardStore((s) => s.lastFetchAt);
  const selectedProfile    = useLeaderboardStore((s) => s.selectedProfile);
  const profileLoading     = useLeaderboardStore((s) => s.profileLoading);
  const setCategory        = useLeaderboardStore((s) => s.setCategory);
  const fetch              = useLeaderboardStore((s) => s.fetch);
  const fetchPlayerProfile = useLeaderboardStore((s) => s.fetchPlayerProfile);
  const clearSelectedProfile = useLeaderboardStore((s) => s.clearSelectedProfile);

  const CATEGORIES = useMemo<Array<{ key: LeaderboardCategory; label: string }>>(() => [
    { key: 'item_power',   label: t.leaderboard.categoryItemPower },
    { key: 'level',        label: t.leaderboard.categoryLevel },
    { key: 'vault_tier',   label: t.leaderboard.categoryVault },
    { key: 'credits',      label: t.leaderboard.categoryCredits },
    { key: 'endless_wave', label: t.leaderboard.categoryEndless },
  ], [t]);

  useEffect(() => {
    void fetch(myId ?? undefined);
  }, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRowClick = (entry: LeaderboardEntry) => {
    void fetchPlayerProfile(entry.id);
  };

  const lastFetchLabel = lastFetchAt > 0
    ? `Aktualisiert ${new Date(lastFetchAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`
    : '';

  return (
    <div className="screen leaderboard-screen">
      <div className="leaderboard__header">
        <h1 className="screen__headline">{t.leaderboard.headline}</h1>
        <button
          className="leaderboard__refresh"
          onClick={() => void fetch(myId ?? undefined)}
          disabled={loading}
          title={lastFetchLabel}
        >
          {loading ? '…' : '↻'}
        </button>
      </div>

      {/* Category chips */}
      <div className="leaderboard__cats">
        {CATEGORIES.map(({ key, label }) => (
          <button
            key={key}
            className={`leaderboard__cat ${category === key ? 'leaderboard__cat--active' : ''}`}
            onClick={() => setCategory(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {lastFetchAt > 0 && (
        <div className="leaderboard__timestamp">{lastFetchLabel}</div>
      )}

      {loading && <div className="leaderboard__state">{t.leaderboard.loading}</div>}

      {!loading && error && (
        <div className="leaderboard__state leaderboard__state--error">
          {t.leaderboard.error}
          <button className="btn btn--ghost leaderboard__retry" onClick={() => void fetch(myId ?? undefined)}>
            {t.leaderboard.refresh}
          </button>
        </div>
      )}

      {!loading && !error && entries.length === 0 && (
        <div className="leaderboard__state">{t.leaderboard.empty}</div>
      )}

      {!loading && !error && entries.length > 0 && (
        <div className="leaderboard__list">
          {entries.map((entry) => {
            const isMe = entry.id === myId;
            const medal = RANK_MEDAL[entry.rank];
            return (
              <button
                key={entry.id}
                className={`leaderboard__row ${isMe ? 'leaderboard__row--me' : ''} ${entry.rank <= 3 ? `leaderboard__row--top${entry.rank}` : ''}`}
                onClick={() => handleRowClick(entry)}
                title={`${entry.runnerName} ansehen`}
              >
                <span className="leaderboard__rank">
                  {medal ?? `#${entry.rank}`}
                </span>
                <span className="leaderboard__name">
                  <PlayerAvatar name={entry.runnerName} avatarColor={entry.avatarColor ?? 'blue'} size="sm" />
                  {entry.runnerName}
                  {isMe && <span className="leaderboard__you">{t.leaderboard.you}</span>}
                </span>
                <span className="leaderboard__secondary">
                  {formatSecondary(entry, category)}
                </span>
                <span className="leaderboard__value">
                  {formatValue(entry, category)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {!loading && myId && myRank !== null && !entries.find((e) => e.id === myId) && (
        <div className="leaderboard__my-rank">
          {t.leaderboard.outsideTop.replace('{rank}', String(myRank))}
        </div>
      )}

      {/* Profile overlay */}
      {profileLoading && (
        <div className="leaderboard__profile-loading">
          <div className="app-loading__spinner" />
        </div>
      )}
      {selectedProfile && (
        <PlayerProfileOverlay
          profile={selectedProfile}
          onClose={clearSelectedProfile}
        />
      )}
    </div>
  );
}
